import { useEffect, useState } from 'react';
import { Loader2, SearchCode, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const BASIS_LABEL = {
  ref_index: 'ref index',
  fallback: 'date+amount fallback',
};

function BasisBadge({ basis }) {
  if (!basis) return <Badge variant="destructive">unmatched</Badge>;
  return <Badge variant="outline">{BASIS_LABEL[basis] || basis}</Badge>;
}

function StatusBadge({ status }) {
  const variant = status === 'matched' ? 'outline' : status === 'ref_matched_amount_mismatch' ? 'secondary' : 'destructive';
  const cls = status === 'ref_matched_amount_mismatch' ? 'text-amber-600' : status === 'matched' ? 'text-emerald-600' : '';
  return <Badge variant={variant} className={cls}>{status || 'unmatched'}</Badge>;
}

export function StatementReconciliationInspectPanel() {
  const [statementId, setStatementId] = useState('');
  const [propertyKey, setPropertyKey] = useState(''); // "identity_type:identity_value"
  const [statementMonth, setStatementMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [properties, setProperties] = useState([]);
  const [propsLoading, setPropsLoading] = useState(false);

  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkApplyResult, setBulkApplyResult] = useState(null);
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const [bulkApplyLoading, setBulkApplyLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setPropsLoading(true);
      try {
        // Dropdown is populated from management_statements' own identities
        // (GET .../property-options), not the portfolio's raw platform ids --
        // property_ref is always Smoobu-id-shaped, so a raw Hostaway id can
        // never be a valid value here (see the route's docstring).
        const { data } = await diagnosticsAPI.statementReconciliationInspectPropertyOptions();
        setProperties(data?.options || []);
      } catch (err) {
        console.error('Failed to load statement property options:', err);
      } finally {
        setPropsLoading(false);
      }
    })();
  }, []);

  const selectedProperty = properties.find(
    (p) => `${p.identity_type}:${p.identity_value}` === propertyKey
  ) || null;

  useEffect(() => {
    setStatementMonth('');
  }, [propertyKey]);

  const canRun = statementId.trim() || (selectedProperty && statementMonth.trim());

  const run = async () => {
    if (!canRun) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const params = statementId.trim()
        ? { statement_id: statementId.trim() }
        : selectedProperty.identity_type === 'ref'
          ? { property_ref: selectedProperty.property_ref, statement_month: statementMonth.trim() }
          : { property_name_raw: selectedProperty.property_name_raw, statement_month: statementMonth.trim() };
      const { data } = await diagnosticsAPI.statementReconciliationInspect(params);
      setResult(data);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runBulkPreview = async () => {
    setBulkPreviewLoading(true);
    setBulkApplyResult(null);
    try {
      const { data } = await diagnosticsAPI.statementReconciliationBulkRecomputePreview();
      setBulkPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBulkPreviewLoading(false);
    }
  };

  const runBulkApply = async () => {
    if (!window.confirm(
      `Recompute and persist reconciliation for ${bulkPreview?.statements_would_change || 0} ` +
      'statement(s) whose Match classification would change? This overwrites their stored ' +
      'reconciliation snapshot and re-stamps matched/unmatched Smoobu reservations.'
    )) return;
    setBulkApplyLoading(true);
    try {
      const { data } = await diagnosticsAPI.statementReconciliationBulkRecomputeApply();
      setBulkApplyResult(data);
      toast.success(`Applied recompute to ${data.statements_applied} statement(s)`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBulkApplyLoading(false);
    }
  };

  const r = result;
  const rows = (r && r.bookings) || [];
  const crossCheck = r && r.instrumentation_cross_check;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <SearchCode className="h-5 w-5 text-blue-600" />
          Statement Reconciliation Inspect
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          For one management statement, shows per booking line exactly which matcher produced the
          Match badge (channel-aware ref index vs date+amount fallback vs unmatched), the
          identifiers and total_price of whatever it matched to, the computed amount diff against
          AMOUNT_MATCH_TOLERANCE, and whether the stored snapshot (from ingest or the last
          manual/bulk re-run) has drifted from a live recompute against current reservation data.
          The single-statement inspect never writes; the bulk recompute below does, gated behind
          an explicit Apply.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Statement id
            </label>
            <Input
              value={statementId}
              onChange={(e) => setStatementId(e.target.value)}
              placeholder="e.g. stmt_gmail_msg_..."
              className="font-mono"
            />
          </div>
          <div className="text-xs text-muted-foreground pb-2">or</div>
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Property
            </label>
            <Select
              value={propertyKey}
              onValueChange={setPropertyKey}
              disabled={!!statementId.trim()}
            >
              <SelectTrigger className="h-9 text-xs" data-testid="stmt-recon-inspect-property-select">
                <SelectValue placeholder="Select property..." />
              </SelectTrigger>
              <SelectContent>
                {propsLoading && (
                  <SelectItem value="_loading" disabled>Loading...</SelectItem>
                )}
                {properties.map((p) => {
                  const key = `${p.identity_type}:${p.identity_value}`;
                  return (
                    <SelectItem key={key} value={key}>
                      <span className="truncate">
                        {p.display_name || '(unnamed)'}
                        {p.resolved_alt_name && (
                          <span className="text-muted-foreground ml-1">
                            &middot; hostaway: {p.resolved_alt_name}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Statement month
            </label>
            <Select
              value={statementMonth}
              onValueChange={setStatementMonth}
              disabled={!!statementId.trim() || !selectedProperty}
            >
              <SelectTrigger className="h-9 text-xs" data-testid="stmt-recon-inspect-month-select">
                <SelectValue placeholder="Select month..." />
              </SelectTrigger>
              <SelectContent>
                {(selectedProperty?.months || []).map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={run} disabled={loading || !canRun} data-testid="stmt-recon-inspect-run">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <SearchCode className="h-4 w-4 mr-2" />
            )}
            Inspect
          </Button>
        </div>

        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}

        {r && (
          <div className="space-y-3" data-testid="stmt-recon-inspect-result">
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground items-center">
              <span>{r.property_name_raw}</span>
              <span>|</span>
              <span>{r.statement_month}</span>
              <span>|</span>
              <span>{r.agency}</span>
              <span>|</span>
              <Badge variant="outline">statement payment: {r.statement_payment_status || 'unset'}</Badge>
              <Badge variant="outline">
                pool: {r.candidate_pool_size?.smoobu ?? 0} smoobu / {r.candidate_pool_size?.hostaway ?? 0} hostaway
              </Badge>
              <Badge variant={crossCheck?.agrees_with_live_recompute ? 'outline' : 'destructive'}>
                mirror {crossCheck?.agrees_with_live_recompute ? 'agrees with' : 'DISAGREES with'} live recompute
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <div className="border rounded-lg p-3">
                <p className="font-medium mb-1">Stored snapshot</p>
                <p className="text-muted-foreground">reconciled_at: {r.reconciliation_snapshot_stored?.reconciled_at || '—'}</p>
                <p className="text-muted-foreground">status: {r.reconciliation_snapshot_stored?.status || '—'}</p>
              </div>
              <div className="border rounded-lg p-3">
                <p className="font-medium mb-1">Live recompute (not persisted)</p>
                <p className="text-muted-foreground">status: {r.reconciliation_live_recompute?.status || '—'}</p>
                <p className="text-muted-foreground">
                  matched Smoobu ids: {(r.reconciliation_live_recompute?.matched_smoobu_ids || []).length}
                </p>
              </div>
            </div>

            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No booking lines on this statement.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-2">Booking ref / channel</th>
                      <th className="text-right p-2">Stmt gross</th>
                      <th className="text-left p-2">Basis</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Matched reservation</th>
                      <th className="text-right p-2">Abs diff / tolerance</th>
                      <th className="text-center p-2">Stored / Live</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const s = row.statement_side;
                      const d = row.match_detail || {};
                      const mr = d.matched_reservation;
                      const stored = row.stored_booking_match_status || {};
                      const live = row.live_booking_match_status || {};
                      return (
                        <tr key={i} className="border-t align-top" data-testid="stmt-recon-inspect-row">
                          <td className="p-2">
                            <div className="font-mono">{s.booking_ref}</div>
                            <div className="text-muted-foreground">{s.channel} · {s.guest_name}</div>
                            <div className="text-muted-foreground">{s.arrival}&rarr;{s.departure}</div>
                          </td>
                          <td className="p-2 text-right font-mono">{s.gross}</td>
                          <td className="p-2"><BasisBadge basis={row.instrumented_basis} /></td>
                          <td className="p-2"><StatusBadge status={row.instrumented_status} /></td>
                          <td className="p-2 font-mono">
                            {mr ? (
                              <>
                                <div>{mr.source}: {mr.source === 'smoobu' ? mr.smoobu_id : mr.hostaway_id}</div>
                                <div className="text-muted-foreground">total_price {mr.total_price}</div>
                              </>
                            ) : '—'}
                          </td>
                          <td className="p-2 text-right font-mono">
                            {d.abs_diff ?? '—'}
                            {d.tolerance_applied != null ? ` / ${d.tolerance_applied}` : ''}
                          </td>
                          <td className="p-2 text-center">
                            <div>{stored.status || '—'}{stored.match_basis ? ` (${stored.match_basis})` : ''}</div>
                            <div>{live.status || '—'}{live.match_basis ? ` (${live.match_basis})` : ''}</div>
                            {row.stale_vs_live && (
                              <div className="text-amber-600">stale</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {crossCheck && !crossCheck.agrees_with_live_recompute && (
              <div className="text-xs text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="font-medium">Instrumentation mismatches (this diagnostic's mirror vs the real matcher):</p>
                <ul className="list-disc list-inside">
                  {crossCheck.mismatches.map((m, i) => (
                    <li key={i}>
                      {m.booking_ref}: mirror says {m.instrumented_status}/{m.instrumented_basis}, live says {m.live_status}/{m.live_match_basis}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ── Bookings-tab diagnostic: statement side / booking side / resolver verdict ── */}
            <div className="border-t pt-3 space-y-3" data-testid="bookings-tab-diagnostic">
              <div>
                <p className="font-medium text-sm mb-1">
                  Statement side -- every statement doc for this property/month
                </p>
                {(r.statement_docs || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No statement docs found for this property/month.</p>
                ) : (
                  <div className="space-y-2">
                    {r.statement_docs.map((sd) => (
                      <div
                        key={sd.statement_id}
                        className={`border rounded-lg p-2 text-xs ${sd.excluded_as_duplicate ? 'border-amber-300 dark:border-amber-700' : ''}`}
                        data-testid="statement-doc-row"
                      >
                        <div className="flex flex-wrap gap-2 items-center mb-1">
                          <span className="font-mono">{sd.statement_id}</span>
                          <Badge variant="outline">source: {sd.source || 'unset'}</Badge>
                          <Badge variant="outline">status: {sd.status || '—'}</Badge>
                          {sd.excluded_as_duplicate && (
                            <Badge variant="secondary" className="text-amber-600">excluded as duplicate</Badge>
                          )}
                          <span className="text-muted-foreground">
                            reconciled_at: {sd.reconciled_at || '—'} via {sd.reconciled_via || 'unknown'}
                          </span>
                        </div>
                        {sd.lines.length > 0 && (
                          <table className="w-full text-xs">
                            <tbody>
                              {sd.lines.map((ln, i) => (
                                <tr key={i} className="border-t">
                                  <td className="p-1 font-mono">{ln.booking_ref}</td>
                                  <td className="p-1">{ln.guest_name}</td>
                                  <td className="p-1 text-right font-mono">{ln.gross}</td>
                                  <td className="p-1">
                                    {ln.match_status.status || '—'}
                                    {ln.match_status.match_basis ? ` (${ln.match_status.match_basis})` : ''}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <p className="font-medium text-sm mb-1">
                  Booking side -- every reservation for this property/month, resolver verdict vs loaded snapshot
                </p>
                {(r.booking_side || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No reservations found for this property/month.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-2">Guest / source</th>
                          <th className="text-left p-2">Ref fields</th>
                          <th className="text-right p-2">Total price</th>
                          <th className="text-center p-2">Quarantined</th>
                          <th className="text-left p-2">Loaded snapshot</th>
                          <th className="text-left p-2">Resolver verdict (live)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.booking_side.map((b, i) => (
                          <tr
                            key={i}
                            className={`border-t align-top ${b.snapshot_vs_resolver_diverge ? 'bg-red-50 dark:bg-red-950/30' : ''}`}
                            data-testid="booking-side-row"
                          >
                            <td className="p-2">
                              <div>{b.guest_name}</div>
                              <div className="text-muted-foreground">{b.source} · {b.channel}</div>
                            </td>
                            <td className="p-2 font-mono text-muted-foreground">
                              {b.ref_fields.channel_reservation_id || b.ref_fields.reference_id || '—'}
                              {' / '}
                              {b.ref_fields.hostaway_id || b.ref_fields.smoobu_id || '—'}
                            </td>
                            <td className="p-2 text-right font-mono">{b.total_price}</td>
                            <td className="p-2 text-center">
                              {b.quarantined ? <Badge variant="destructive">quarantined</Badge> : '—'}
                            </td>
                            <td className="p-2">
                              {b.loaded_statement_snapshot
                                ? `${b.loaded_statement_snapshot.status}${b.loaded_statement_snapshot.match_basis ? ` (${b.loaded_statement_snapshot.match_basis})` : ''}`
                                : '—'}
                            </td>
                            <td className="p-2">
                              {b.resolver_verdict.status || '—'}
                              {b.resolver_verdict.match_basis ? ` (${b.resolver_verdict.match_basis})` : ''}
                              {b.snapshot_vs_resolver_diverge && (
                                <span className="text-red-600 dark:text-red-400 font-medium"> · diverges</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Bulk recompute (all statements) ── */}
        <div className="border-t pt-4 space-y-2">
          <h4 className="font-medium text-sm flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Bulk recompute (all statements)
          </h4>
          <p className="text-xs text-muted-foreground">
            Recomputes every management statement against current reservation data using the same
            reconcile_statement() a single statement's Re-reconcile button calls -- no property or
            landlord re-resolution. Front door for upgrading statements reconciled before the
            ref-index/amount-gate fix. Preview never writes.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={runBulkPreview} disabled={bulkPreviewLoading} data-testid="stmt-recon-bulk-preview">
              {bulkPreviewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Preview
            </Button>
            {bulkPreview && bulkPreview.statements_would_change > 0 && (
              <Button
                variant="default"
                size="sm"
                onClick={runBulkApply}
                disabled={bulkApplyLoading || !!bulkApplyResult}
                data-testid="stmt-recon-bulk-apply"
              >
                {bulkApplyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Apply to {bulkPreview.statements_would_change} Statement{bulkPreview.statements_would_change === 1 ? '' : 's'}
              </Button>
            )}
          </div>

          {bulkPreview && (
            <div className="space-y-2 text-xs" data-testid="stmt-recon-bulk-preview-result">
              <div className="flex flex-wrap gap-2 items-center">
                <Badge variant="outline">scanned: {bulkPreview.statements_scanned}</Badge>
                <Badge variant={bulkPreview.statements_would_change > 0 ? 'secondary' : 'outline'}>
                  would change: {bulkPreview.statements_would_change}
                </Badge>
                <Badge variant="outline">newly ref-matched: {bulkPreview.newly_ref_matched_count}</Badge>
                <Badge variant="outline">newly amount-mismatched: {bulkPreview.newly_amount_mismatch_count}</Badge>
                {bulkPreview.truncated && (
                  <Badge variant="destructive">truncated at scan cap {bulkPreview.scan_cap}</Badge>
                )}
                {bulkApplyResult && (
                  <Badge variant="outline" className="text-emerald-600">applied: {bulkApplyResult.statements_applied}</Badge>
                )}
              </div>

              {bulkPreview.statements.filter((s) => s.would_change).length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2">Statement</th>
                        <th className="text-left p-2">Old / New status</th>
                        <th className="text-left p-2">Newly ref-matched</th>
                        <th className="text-left p-2">Newly amount-mismatched</th>
                        <th className="text-left p-2">Other changed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkPreview.statements.filter((s) => s.would_change).map((s) => (
                        <tr key={s.statement_id} className="border-t align-top">
                          <td className="p-2">
                            <div className="font-mono">{s.statement_id}</div>
                            <div className="text-muted-foreground">{s.property_name_raw} · {s.statement_month}</div>
                          </td>
                          <td className="p-2">{s.old_status || '—'} → {s.new_status}</td>
                          <td className="p-2 font-mono">{(s.diff.newly_ref_matched || []).join(', ') || '—'}</td>
                          <td className="p-2 font-mono">{(s.diff.newly_amount_mismatch || []).join(', ') || '—'}</td>
                          <td className="p-2 font-mono">{(s.diff.other_changed || []).join(', ') || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
